import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  FormControl,
  IconButton,
  TextField,
  Button,
  ButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Stack,
  Card,
  Paper,
  Divider,
  MenuItem,
  Select,
  InputLabel,
  List,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Close as CloseIcon,
  Assignment as TaskIcon,
  FilterList as FilterListIcon,
  AttachFile as AttachFileIcon,
  CalendarToday as CalendarIcon,
  ListAlt as ListIcon,
  Notes as NoteIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { STATUS_COLORS, getStatusDisplay } from '../utils/taskColors';
import RichTextEditor from './RichTextEditor';
import { AudioRecorder } from './AudioRecorder';

// Import extracted types, constants, and utilities
import type { TimelineEvent, CaseTimelineProps, InlineEntryFormProps } from './CaseTimeline/types/timeline.types';

// Import custom hooks
import { useTimelineEvents } from './CaseTimeline/hooks/useTimelineEvents';
import { useEmailFeatures } from './CaseTimeline/hooks/useEmailFeatures';
import { useInlineEntry } from './CaseTimeline/hooks/useInlineEntry';

// Import sub-components
import { TaskDeleteDialog } from './CaseTimeline/components/TaskDeleteDialog';
import { FilterMenu } from './CaseTimeline/components/FilterMenu';
import { EmailSummaryPopover } from './CaseTimeline/components/EmailSummaryPopover';
import { EmailTagDialog } from './CaseTimeline/components/EmailTagDialog';
import { LogEntryCard } from './CaseTimeline/components/LogEntryCard';
import { StandardEventCard } from './CaseTimeline/components/StandardEventCard';
import { TaskEditDialog } from './CaseTimeline/components/TaskEditDialog';

// Memoized inline entry form component with custom comparison
const InlineEntryForm = React.memo<InlineEntryFormProps>(({
  caseId,
  inlineEntryType,
  setInlineEntryType,
  editorKey,
  setEditorKey,
  inlineEditorContent,
  setInlineEditorContent,
  inlineTaskTitle,
  setInlineTaskTitle,
  inlineTaskPriority,
  setInlineTaskPriority,
  inlineTaskAssignedTo,
  setInlineTaskAssignedTo,
  inlineTaskDueDate,
  setInlineTaskDueDate,
  inlineLogDate,
  setInlineLogDate,
  inlineFiles,
  setInlineFiles,
  availableUsers,
  pendingTasks,
  loadingPendingTasks,
  onEditTask,
  onDeleteTask,
  handleTranscriptionComplete,
  handleTranscriptionError,
  handleCreateInlineLog,
}) => {
  return (
    <Box sx={{
      mb: 3,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      p: 2,
      backgroundColor: 'background.paper',
      boxShadow: 3,
      minHeight: 400
    }}>
      {/* Toggle between Case Log, Task, and Pending Tasks */}
      <ButtonGroup sx={{ mb: 2 }} size="small">
        <Button
          variant={inlineEntryType === 'log' ? 'contained' : 'outlined'}
          onClick={() => {
            setInlineEntryType('log');
            setEditorKey(prev => prev + 1); // Force remount with new placeholder
          }}
          startIcon={<NoteIcon />}
        >
          Case Log
        </Button>
        <Button
          variant={inlineEntryType === 'task' ? 'contained' : 'outlined'}
          onClick={() => {
            setInlineEntryType('task');
            setEditorKey(prev => prev + 1); // Force remount with new placeholder
          }}
          startIcon={<TaskIcon />}
        >
          Task
        </Button>
        <Button
          variant={inlineEntryType === 'pending' ? 'contained' : 'outlined'}
          onClick={() => {
            setInlineEntryType('pending');
          }}
          startIcon={<ListIcon />}
        >
          Pending Tasks
        </Button>
      </ButtonGroup>

      {/* Show form fields for log and task, hide for pending */}
      {inlineEntryType !== 'pending' && (
        <>
          {/* Audio Recorder (only for case logs) */}
          {inlineEntryType === 'log' && caseId && (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AudioRecorder
                caseId={parseInt(caseId)}
                onTranscriptionComplete={handleTranscriptionComplete}
                onError={handleTranscriptionError}
              />
              <Typography variant="caption" color="text.secondary">
                Record a voice note
              </Typography>
            </Box>
          )}

          {/* Task Title (only for tasks) */}
          {inlineEntryType === 'task' && (
            <TextField
              fullWidth
              placeholder="Task title (required)"
              value={inlineTaskTitle}
              onChange={(e) => setInlineTaskTitle(e.target.value)}
              sx={{ mb: 2 }}
              size="small"
            />
          )}

          {/* Shared Rich Text Editor */}
          <RichTextEditor
            key={editorKey}
            initialContent={inlineEditorContent}
            onChange={(content) => setInlineEditorContent(content)}
            placeholder={inlineEntryType === 'task' ? 'Add a description for this task...' : 'Start typing to add a new case log entry...'}
            minHeight={120}
            showSaveButton={false}
            hideAttachmentButton={true}
          />

          {/* Context-specific fields */}
          {inlineEntryType === 'task' ? (
            <Stack direction="row" spacing={2} sx={{ mt: 2 }} flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={inlineTaskPriority}
                  onChange={(e) => setInlineTaskPriority(e.target.value)}
                  label="Priority"
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="inline-assign-to-label">Assign To</InputLabel>
                <Select
                  labelId="inline-assign-to-label"
                  value={inlineTaskAssignedTo}
                  onChange={(e) => setInlineTaskAssignedTo(e.target.value)}
                  label="Assign To"
                >
                  <MenuItem value="">
                    <em>Unassigned</em>
                  </MenuItem>
                  {availableUsers.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.first_name && user.last_name
                        ? `${user.first_name} ${user.last_name}`
                        : user.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                type="date"
                label="Due Date"
                size="small"
                value={inlineTaskDueDate}
                onChange={(e) => setInlineTaskDueDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 150 }}
              />
            </Stack>
          ) : (
            <Box sx={{ mt: 2 }}>
              <TextField
                type="datetime-local"
                label="Date & Time"
                size="small"
                value={inlineLogDate}
                onChange={(e) => setInlineLogDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 250 }}
              />
            </Box>
          )}

          {/* Attached Files List */}
          {inlineFiles.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                Attached Files ({inlineFiles.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {inlineFiles.map((file, index) => (
                  <Chip
                    key={index}
                    label={file.name}
                    onDelete={() => {
                      setInlineFiles(inlineFiles.filter((_, i) => i !== index));
                    }}
                    size="small"
                    icon={<AttachFileIcon />}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* File Upload and Save Button on same line */}
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Button
              component="label"
              variant="outlined"
              size="small"
              startIcon={<AttachFileIcon />}
            >
              Attach Files
              <input
                type="file"
                multiple
                hidden
                onChange={(e) => {
                  if (e.target.files) {
                    // Append new files to existing ones instead of replacing
                    setInlineFiles([...inlineFiles, ...Array.from(e.target.files)]);
                  }
                }}
              />
            </Button>

            <Button
              variant="contained"
              onClick={handleCreateInlineLog}
              disabled={
                inlineEntryType === 'task'
                  ? !inlineTaskTitle.trim()
                  : !inlineEditorContent.trim()
              }
            >
              {inlineEntryType === 'task' ? 'Create Task' : 'Add Log Entry'}
            </Button>
          </Box>
        </>
      )}

      {/* Show pending tasks view */}
      {inlineEntryType === 'pending' && (
        <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
          {loadingPendingTasks ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : pendingTasks.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No pending tasks for this case
            </Typography>
          ) : (
            <List sx={{ p: 0 }}>
              {pendingTasks.map((task) => (
                <Card
                  key={task.id}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: 2,
                      backgroundColor: 'action.hover',
                    },
                    transition: 'box-shadow 0.2s, background-color 0.2s',
                  }}
                  onClick={() => onEditTask(task.id)}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    {/* Task Icon */}
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: STATUS_COLORS[task.status as keyof typeof STATUS_COLORS] === 'warning' ? '#FF6F00' : '#FFA726',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        color: 'white',
                      }}
                    >
                      <TaskIcon fontSize="small" />
                    </Box>

                    {/* Title and metadata */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {task.title}
                        </Typography>

                        {/* Due Date Pill */}
                        {task.due_date && (
                          <Chip
                            label={`Due ${new Date(task.due_date).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: new Date(task.due_date).getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
                            })}`}
                            size="small"
                            icon={<CalendarIcon sx={{ fontSize: 14 }} />}
                            sx={{
                              height: 22,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              backgroundColor: task.is_overdue ? 'error.main' : 'info.main',
                              color: 'white',
                              '& .MuiChip-icon': {
                                color: 'white',
                              }
                            }}
                          />
                        )}
                      </Box>

                      {/* Assignee row */}
                      {task.assigned_to_name && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {task.assigned_to_name}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Status badge */}
                    <Chip
                      label={getStatusDisplay(task.status)}
                      size="small"
                      color={STATUS_COLORS[task.status as keyof typeof STATUS_COLORS] || 'default'}
                      sx={{ height: 20, fontSize: '0.7rem', fontWeight: 500 }}
                    />

                    {/* Priority badge */}
                    <Chip
                      label={task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
                      size="small"
                      sx={{ height: 20, fontSize: '0.7rem', fontWeight: 500 }}
                    />

                    {/* Delete button */}
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteTask(task);
                      }}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'error.light',
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Card>
              ))}
            </List>
          )}
        </Box>
      )}
    </Box>
  );
}, (prevProps, nextProps) => {
  // Custom comparison to prevent re-renders when only function references change
  // Only re-render if value props or key state props change
  return (
    prevProps.caseId === nextProps.caseId &&
    prevProps.inlineEntryType === nextProps.inlineEntryType &&
    prevProps.editorKey === nextProps.editorKey &&
    prevProps.inlineEditorContent === nextProps.inlineEditorContent &&
    prevProps.inlineTaskTitle === nextProps.inlineTaskTitle &&
    prevProps.inlineTaskPriority === nextProps.inlineTaskPriority &&
    prevProps.inlineTaskAssignedTo === nextProps.inlineTaskAssignedTo &&
    prevProps.inlineTaskDueDate === nextProps.inlineTaskDueDate &&
    prevProps.inlineLogDate === nextProps.inlineLogDate &&
    prevProps.inlineFiles === nextProps.inlineFiles &&
    prevProps.availableUsers === nextProps.availableUsers &&
    prevProps.pendingTasks === nextProps.pendingTasks &&
    prevProps.loadingPendingTasks === nextProps.loadingPendingTasks
  );
});

InlineEntryForm.displayName = 'InlineEntryForm';

export default function CaseTimeline({ caseId }: CaseTimelineProps) {
  const { access } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${access}` }), [access]);

  // Shared error state
  const [error, setError] = useState<string | null>(null);

  // Use custom hooks for organized state management
  const timeline = useTimelineEvents({ caseId, headers });
  const email = useEmailFeatures({
    caseId,
    headers,
    fetchTimelineEvents: timeline.fetchTimelineEvents,
    setError
  });
  const inline = useInlineEntry({
    caseId,
    headers,
    fetchTimelineEvents: timeline.fetchTimelineEvents,
    setError
  });

  // Destructure commonly used values from timeline hook
  const {
    events,
    loading,
    selectedFilters,
    filterAnchorEl,
    expandedEvents,
    fetchTimelineEvents,
    toggleEventExpanded,
    handleFilterToggle,
    handleSelectAll,
    handleDeselectAll,
    handleFilterMenuOpen,
    handleFilterMenuClose,
    handleCollapseAll,
    ENTITY_EVENTS,
    DEFENDANT_EVENTS,
    DOCUMENT_EVENTS,
    TASK_EVENTS,
    TEAM_EVENTS,
    EMAIL_EVENTS,
  } = timeline;

  // States not yet extracted to hooks (event editing, log editing, task editing, dialogs)
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [editEventDate, setEditEventDate] = useState<string>('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<TimelineEvent | null>(null);

  // Case log editing state
  const [editLogContent, setEditLogContent] = useState<string>('');
  const [editLogFiles, setEditLogFiles] = useState<File[]>([]);
  const [editLogExistingAttachments, setEditLogExistingAttachments] = useState<any[]>([]);
  const [editLogAttachmentsToRemove, setEditLogAttachmentsToRemove] = useState<string[]>([]);

  // Task editing state (for timeline tasks)
  const [editTaskTitle, setEditTaskTitle] = useState<string>('');
  const [editTaskDescription, setEditTaskDescription] = useState<string>('');
  const [editTaskStatus, setEditTaskStatus] = useState<string>('');
  const [editTaskPriority, setEditTaskPriority] = useState<string>('');
  const [editTaskAssignedTo, setEditTaskAssignedTo] = useState<string>('');
  const [editTaskDueDate, setEditTaskDueDate] = useState<string>('');
  const [editTaskFiles, setEditTaskFiles] = useState<File[]>([]);
  const [editTaskExistingAttachments, setEditTaskExistingAttachments] = useState<any[]>([]);
  const [editTaskAttachmentsToRemove, setEditTaskAttachmentsToRemove] = useState<string[]>([]);

  // Pending task edit dialog state
  const [taskEditDialogOpen, setTaskEditDialogOpen] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [loadingTaskData, setLoadingTaskData] = useState(false);

  // Task edit modal state (for pending tasks tab)
  const [editTaskModalOpen, setEditTaskModalOpen] = useState(false);
  const [selectedTaskForEdit, _setSelectedTaskForEdit] = useState<any>(null);
  const [editedTaskTitle, setEditedTaskTitle] = useState('');
  const [editedTaskDescription, setEditedTaskDescription] = useState('');
  const [editedTaskStatus, setEditedTaskStatus] = useState('');
  const [editedTaskPriority, setEditedTaskPriority] = useState('');
  const [editedTaskDueDate, setEditedTaskDueDate] = useState('');
  const [editedTaskAssignedTo, setEditedTaskAssignedTo] = useState('');
  const [editedTaskFiles, setEditedTaskFiles] = useState<File[]>([]);
  const [editedTaskExistingAttachments, setEditedTaskExistingAttachments] = useState<any[]>([]);
  const [editedTaskAttachmentsToRemove, setEditedTaskAttachmentsToRemove] = useState<string[]>([]);
  const [updatingTask, setUpdatingTask] = useState(false);

  // Task delete state
  const [deleteTaskConfirmOpen, setDeleteTaskConfirmOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<any>(null);
  const [deletingTask, setDeletingTask] = useState(false);

  // Handle pending task actions - open edit dialog
  const handleEditTaskFromPending = async (taskId: string) => {
    setLoadingTaskData(true);
    setTaskEditDialogOpen(true);
    setEditingTaskId(taskId);

    try {
      const response = await api.get(`/tasks/${taskId}/`, { headers });
      const taskData = response.data;

      setEditTaskTitle(taskData.title || '');
      setEditTaskDescription(taskData.description || '');
      setEditTaskStatus(taskData.status || 'pending');
      setEditTaskPriority(taskData.priority || 'medium');
      setEditTaskAssignedTo(taskData.assigned_to || '');
      setEditTaskDueDate(taskData.due_date ? taskData.due_date.split('T')[0] : '');
      setEditTaskFiles([]);
      setEditTaskExistingAttachments(taskData.attachments || []);
      setEditTaskAttachmentsToRemove([]);
    } catch (err) {
      console.error('Failed to fetch task:', err);
      setError('Failed to load task for editing');
      setTaskEditDialogOpen(false);
    } finally {
      setLoadingTaskData(false);
    }
  };

  const handleCloseTaskEditDialog = () => {
    setTaskEditDialogOpen(false);
    setEditingTaskId(null);
    setEditTaskTitle('');
    setEditTaskDescription('');
    setEditTaskStatus('');
    setEditTaskPriority('');
    setEditTaskAssignedTo('');
    setEditTaskDueDate('');
    setEditTaskFiles([]);
    setEditTaskExistingAttachments([]);
    setEditTaskAttachmentsToRemove([]);
  };

  const handleSaveTaskFromDialog = async () => {
    if (!editingTaskId) return;

    setLoadingTaskData(true);
    try {
      const formData = new FormData();
      formData.append('title', editTaskTitle);
      formData.append('description', editTaskDescription);
      formData.append('status', editTaskStatus);
      formData.append('priority', editTaskPriority);
      formData.append('assigned_to', editTaskAssignedTo);
      if (editTaskDueDate) {
        formData.append('due_date', editTaskDueDate);
      }

      // Add new files (backend expects 'attachment_files' field name)
      editTaskFiles.forEach((file) => {
        formData.append('attachment_files', file);
      });

      // Add attachments to remove
      if (editTaskAttachmentsToRemove.length > 0) {
        formData.append('attachments_to_remove', JSON.stringify(editTaskAttachmentsToRemove));
      }

      await api.patch(`/tasks/${editingTaskId}/`, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data',
        },
      });

      // Refetch the task to get the updated attachments
      const refreshResponse = await api.get(`/tasks/${editingTaskId}/`, { headers });
      const updatedTaskData = refreshResponse.data;

      setEditTaskFiles([]);
      setEditTaskExistingAttachments(updatedTaskData.attachments || []);
      setEditTaskAttachmentsToRemove([]);

      // Refresh pending tasks by resetting the cache flag
      if (inline.setPendingTasksFetched) {
        inline.setPendingTasksFetched(false);
      }
    } catch (err) {
      console.error('Failed to update task:', err);
      setError('Failed to update task');
    } finally {
      setLoadingTaskData(false);
    }
  };

  const handleDeleteAttachmentFromDialog = (attachmentId: string) => {
    setEditTaskAttachmentsToRemove([...editTaskAttachmentsToRemove, attachmentId]);
    setEditTaskExistingAttachments(editTaskExistingAttachments.filter(a => a.id !== attachmentId));
  };

  const handleDeleteTaskClick = (task: any) => {
    setTaskToDelete(task);
    setDeleteTaskConfirmOpen(true);
  };

  // Handle event date edit
  const handleEditEvent = (event: TimelineEvent) => {
    setEditingEventId(event.id);
    // Convert ISO datetime to datetime-local format (YYYY-MM-DDTHH:MM)
    const date = new Date(event.event_date);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const datetimeLocal = `${year}-${month}-${day}T${hours}:${minutes}`;
    setEditEventDate(datetimeLocal);
  };

  const handleSaveEventDate = async (eventId: string) => {
    try {
      // Convert datetime-local to ISO format
      const isoDate = new Date(editEventDate).toISOString();
      await api.patch(`/cases/${caseId}/timeline/${eventId}/`, {
        event_date: isoDate
      }, { headers });

      await fetchTimelineEvents();
      setEditingEventId(null);
    } catch (err) {
      console.error('Failed to update event date:', err);
      setError('Failed to update event date');
    }
  };

  const handleCancelEdit = () => {
    setEditingEventId(null);
    setEditEventDate('');
    setEditLogContent('');
    setEditLogFiles([]);
    setEditLogExistingAttachments([]);
    setEditLogAttachmentsToRemove([]);
    setEditTaskTitle('');
    setEditTaskDescription('');
    setEditTaskStatus('');
    setEditTaskPriority('');
    setEditTaskAssignedTo('');
    setEditTaskDueDate('');
    setEditTaskFiles([]);
    setEditTaskExistingAttachments([]);
    setEditTaskAttachmentsToRemove([]);
    email.setEditingEmailTitleId(null);
    email.setEditEmailTitle('');
  };

  // Handle email title editing

  // Handle case log editing
  const handleEditLogEntry = async (event: TimelineEvent) => {
    // Fetch the actual log entry data first
    if (event.related_object_id) {
      try {
        const response = await api.get(`/cases/${caseId}/log/${event.related_object_id}/`, { headers });
        const logData = response.data;

        // Set all the edit state values with datetime-local format
        const date = new Date(event.event_date);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const datetimeLocal = `${year}-${month}-${day}T${hours}:${minutes}`;

        setEditEventDate(datetimeLocal);
        setEditLogContent(logData.content || '');
        setEditLogFiles([]);
        setEditLogExistingAttachments(logData.attachments || []);

        // Now set editing mode to show the form
        setEditingEventId(event.id);
      } catch (err) {
        console.error('Failed to fetch log entry:', err);
        setError('Failed to load log entry for editing');
      }
    }
  };

  const handleSaveLogEntry = async (event: TimelineEvent) => {
    if (!event.related_object_id) return;

    try {
      // Ensure content is a string (could be object from backend or string from editor)
      const contentToSend = typeof editLogContent === 'string'
        ? editLogContent
        : JSON.stringify(editLogContent);

      // Use FormData if there are files to upload or attachments to remove
      if (editLogFiles.length > 0 || editLogAttachmentsToRemove.length > 0) {
        const formData = new FormData();
        formData.append('content', contentToSend);
        formData.append('log_date', editEventDate);

        // Append all new files
        editLogFiles.forEach((file) => {
          formData.append('attachment_files', file);
        });

        // Append attachments to remove
        editLogAttachmentsToRemove.forEach((attachmentId) => {
          formData.append('attachments_to_remove', attachmentId);
        });

        await api.patch(`/cases/${caseId}/log/${event.related_object_id}/`, formData, {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data',
          },
        });
      } else {
        // Use JSON payload when no files
        await api.patch(`/cases/${caseId}/log/${event.related_object_id}/`, {
          content: contentToSend,
          log_date: editEventDate
        }, { headers });
      }

      await fetchTimelineEvents();
      setEditingEventId(null);
      handleCancelEdit();
    } catch (err) {
      console.error('Failed to update log entry:', err);
      setError('Failed to update log entry');
    }
  };

  // Handle task editing
  const handleEditTask = async (event: TimelineEvent) => {
    // Fetch the actual task data first
    if (event.related_object_id) {
      try {
        const response = await api.get(`/tasks/${event.related_object_id}/`, { headers });
        const taskData = response.data;

        // Set all the edit state values
        const dateOnly = event.event_date.split('T')[0];
        setEditEventDate(dateOnly);
        setEditTaskTitle(taskData.title || '');
        setEditTaskDescription(taskData.description || '');
        setEditTaskStatus(taskData.status || 'pending');
        setEditTaskPriority(taskData.priority || 'medium');
        setEditTaskAssignedTo(taskData.assigned_to || '');
        setEditTaskDueDate(taskData.due_date ? taskData.due_date.split('T')[0] : '');
        setEditTaskFiles([]);
        setEditTaskExistingAttachments(taskData.attachments || []);

        // Now set editing mode to show the form
        setEditingEventId(event.id);
      } catch (err) {
        console.error('Failed to fetch task:', err);
        setError('Failed to load task for editing');
      }
    }
  };

  const handleSaveTask = async (event: TimelineEvent) => {
    if (!event.related_object_id) return;

    try {
      // Ensure description is a string (could be object from backend or string from editor)
      const descriptionToSend = typeof editTaskDescription === 'string'
        ? editTaskDescription
        : JSON.stringify(editTaskDescription);

      // Use FormData if there are files to upload or attachments to remove
      if (editTaskFiles.length > 0 || editTaskAttachmentsToRemove.length > 0) {
        const formData = new FormData();
        formData.append('title', editTaskTitle.trim());
        formData.append('description', descriptionToSend);
        formData.append('status', editTaskStatus);
        formData.append('priority', editTaskPriority);

        if (editTaskDueDate) {
          formData.append('due_date', editTaskDueDate);
        }

        if (editTaskAssignedTo) {
          formData.append('assigned_to', editTaskAssignedTo);
        }

        // Append all new files
        editTaskFiles.forEach((file) => {
          formData.append('attachment_files', file);
        });

        // Append attachments to remove
        editTaskAttachmentsToRemove.forEach((attachmentId) => {
          formData.append('attachments_to_remove', attachmentId);
        });

        await api.patch(`/tasks/${event.related_object_id}/`, formData, {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data',
          },
        });
      } else {
        // Use JSON payload when no files
        const payload: any = {
          title: editTaskTitle,
          description: descriptionToSend,
          status: editTaskStatus,
          priority: editTaskPriority,
        };

        if (editTaskDueDate) {
          payload.due_date = new Date(editTaskDueDate).toISOString();
        }

        if (editTaskAssignedTo) {
          payload.assigned_to = editTaskAssignedTo;
        }

        await api.patch(`/tasks/${event.related_object_id}/`, payload, { headers });
      }

      await fetchTimelineEvents();
      setEditingEventId(null);
      handleCancelEdit();
    } catch (err) {
      console.error('Failed to update task:', err);
      setError('Failed to update task');
    }
  };

  // Handle event deletion
  const handleDeleteClick = (event: TimelineEvent) => {
    setEventToDelete(event);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!eventToDelete) return;

    try {
      await api.delete(`/cases/${caseId}/timeline/${eventToDelete.id}/`, { headers });
      await fetchTimelineEvents();
      setDeleteDialogOpen(false);
      setEventToDelete(null);
    } catch (err) {
      console.error('Failed to delete timeline event:', err);
      setError('Failed to delete timeline event');
      setDeleteDialogOpen(false);
      setEventToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setEventToDelete(null);
  };

  // Handle email summarization

  // Event type filter options - consolidated by category
  const eventTypeOptions = [
    { value: '', label: 'All Events' },
    { value: 'log_entry', label: 'Case Logs' },
    { value: 'tasks', label: 'Tasks' },
    { value: 'emails', label: 'Emails' },
    { value: 'documents', label: 'Documents' },
    { value: 'entities', label: 'Entities' },
    { value: 'defendants', label: 'Defendants' },
    { value: 'team', label: 'Team Members' },
    { value: 'case_created', label: 'Case Created' },
    { value: 'status_changed', label: 'Status Changed' },
  ];

  const filterMenuOpen = Boolean(filterAnchorEl);

  return (
    <Box sx={{ px: 3, pt: 0, pb: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Inline Entry Creator with Toggle */}
      <InlineEntryForm
        caseId={caseId}
        inlineEntryType={inline.inlineEntryType}
        setInlineEntryType={inline.setInlineEntryType}
        editorKey={inline.editorKey}
        setEditorKey={inline.setEditorKey}
        inlineEditorContent={inline.inlineEditorContent}
        setInlineEditorContent={inline.setInlineEditorContent}
        inlineTaskTitle={inline.inlineTaskTitle}
        setInlineTaskTitle={inline.setInlineTaskTitle}
        inlineTaskPriority={inline.inlineTaskPriority}
        setInlineTaskPriority={inline.setInlineTaskPriority}
        inlineTaskAssignedTo={inline.inlineTaskAssignedTo}
        setInlineTaskAssignedTo={inline.setInlineTaskAssignedTo}
        inlineTaskDueDate={inline.inlineTaskDueDate}
        setInlineTaskDueDate={inline.setInlineTaskDueDate}
        inlineLogDate={inline.inlineLogDate}
        setInlineLogDate={inline.setInlineLogDate}
        inlineFiles={inline.inlineFiles}
        setInlineFiles={inline.setInlineFiles}
        availableUsers={inline.availableUsers}
        pendingTasks={inline.pendingTasks}
        loadingPendingTasks={inline.loadingPendingTasks}
        onEditTask={handleEditTaskFromPending}
        onDeleteTask={handleDeleteTaskClick}
        handleTranscriptionComplete={inline.handleTranscriptionComplete}
        handleTranscriptionError={inline.handleTranscriptionError}
        handleCreateInlineLog={inline.handleCreateInlineLog}
      />

      {/* Filter header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 4,
          mt: 5,
          pb: 3,
          borderBottom: '2px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', flexShrink: 0 }}>
          Timeline
        </Typography>
        <Button
          variant="outlined"
          size="medium"
          startIcon={<FilterListIcon />}
          onClick={handleFilterMenuOpen}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            px: 2,
          }}
        >
          Events
        </Button>
        <Button
          variant="outlined"
          size="medium"
          onClick={handleCollapseAll}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            px: 2,
            ml: 'auto',
          }}
        >
          Collapse All
        </Button>
      </Box>

      {/* Filter Menu */}
      <FilterMenu
        open={filterMenuOpen}
        anchorEl={filterAnchorEl}
        selectedFilters={selectedFilters}
        filterOptions={eventTypeOptions}
        onClose={handleFilterMenuClose}
        onFilterToggle={handleFilterToggle}
        onSelectAll={handleSelectAll}
        onDeselectAll={handleDeselectAll}
      />

      {/* Expandable timeline cards */}
      <Box sx={{ minHeight: 600 }}>
        {loading && events.length === 0 ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : events.length === 0 ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No timeline events found
            </Typography>
          </Box>
        ) : (
          <Stack spacing={0}>
            {events.map((event, index) => {
            const isExpanded = expandedEvents.has(event.id);
            const isEditing = editingEventId === event.id;
            const isLastEvent = index === events.length - 1;

            const isNewlyCreated = event.id === inline.newlyCreatedEventId;

            // Special layout for log entries
            if (event.event_type === 'log_entry') {
              return (
                <LogEntryCard
                  key={event.id}
                  event={event}
                  isEditing={isEditing}
                  isNewlyCreated={isNewlyCreated}
                  isLastEvent={isLastEvent}
                  headers={headers}
                  editEventDate={editEventDate}
                  editLogContent={editLogContent}
                  editLogFiles={editLogFiles}
                  editLogExistingAttachments={editLogExistingAttachments}
                  editLogAttachmentsToRemove={editLogAttachmentsToRemove}
                  setEditEventDate={setEditEventDate}
                  setEditLogContent={setEditLogContent}
                  setEditLogFiles={setEditLogFiles}
                  setEditLogExistingAttachments={setEditLogExistingAttachments}
                  setEditLogAttachmentsToRemove={setEditLogAttachmentsToRemove}
                  onStartEdit={handleEditLogEntry}
                  onCancelEdit={handleCancelEdit}
                  onSave={handleSaveLogEntry}
                  onDelete={handleDeleteClick}
                />
              );
            }

            // Standard layout for all other event types
            return (
              <StandardEventCard
                key={event.id}
                event={event}
                isExpanded={isExpanded}
                isEditing={isEditing}
                isNewlyCreated={isNewlyCreated}
                isLastEvent={isLastEvent}
                caseId={caseId}
                headers={headers}
                email={email}
                EMAIL_EVENTS={EMAIL_EVENTS}
                TASK_EVENTS={TASK_EVENTS}
                DOCUMENT_EVENTS={DOCUMENT_EVENTS}
                ENTITY_EVENTS={ENTITY_EVENTS}
                DEFENDANT_EVENTS={DEFENDANT_EVENTS}
                TEAM_EVENTS={TEAM_EVENTS}
                editEventDate={editEventDate}
                editLogContent={editLogContent}
                editLogFiles={editLogFiles}
                editLogExistingAttachments={editLogExistingAttachments}
                editLogAttachmentsToRemove={editLogAttachmentsToRemove}
                editTaskTitle={editTaskTitle}
                editTaskDescription={editTaskDescription}
                editTaskStatus={editTaskStatus}
                editTaskPriority={editTaskPriority}
                editTaskAssignedTo={editTaskAssignedTo}
                editTaskDueDate={editTaskDueDate}
                editTaskFiles={editTaskFiles}
                editTaskExistingAttachments={editTaskExistingAttachments}
                editTaskAttachmentsToRemove={editTaskAttachmentsToRemove}
                setEditEventDate={setEditEventDate}
                setEditLogContent={setEditLogContent}
                setEditLogFiles={setEditLogFiles}
                setEditLogExistingAttachments={setEditLogExistingAttachments}
                setEditLogAttachmentsToRemove={setEditLogAttachmentsToRemove}
                setEditTaskTitle={setEditTaskTitle}
                setEditTaskDescription={setEditTaskDescription}
                setEditTaskStatus={setEditTaskStatus}
                setEditTaskPriority={setEditTaskPriority}
                setEditTaskAssignedTo={setEditTaskAssignedTo}
                setEditTaskDueDate={setEditTaskDueDate}
                setEditTaskFiles={setEditTaskFiles}
                setEditTaskExistingAttachments={setEditTaskExistingAttachments}
                setEditTaskAttachmentsToRemove={setEditTaskAttachmentsToRemove}
                availableUsers={inline.availableUsers}
                toggleEventExpanded={toggleEventExpanded}
                handleEditLogEntry={handleEditLogEntry}
                handleEditTask={handleEditTask}
                handleEditEvent={handleEditEvent}
                handleSaveLogEntry={handleSaveLogEntry}
                handleSaveTask={handleSaveTask}
                handleSaveEventDate={handleSaveEventDate}
                handleCancelEdit={handleCancelEdit}
                handleDeleteClick={handleDeleteClick}
              />
            );
          })}
          </Stack>
        )}
      </Box>

      {/* Eliot Summary Popover */}
      <EmailSummaryPopover
        anchorEl={email.summaryPopoverAnchor}
        emailSummaries={email.emailSummaries}
        onClose={email.handleCloseSummaryPopover}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {eventToDelete?.event_type === 'log_entry'
            ? 'Delete Case Log Entry?'
            : eventToDelete?.event_type.includes('task')
            ? 'Delete Task?'
            : 'Delete Event?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {eventToDelete?.event_type === 'log_entry'
              ? 'Are you sure you want to delete this case log entry? This will permanently remove the log entry and all its attachments. This action cannot be undone.'
              : eventToDelete?.event_type.includes('task')
              ? 'Are you sure you want to delete this task? This will permanently remove the task from the system. This action cannot be undone.'
              : 'Are you sure you want to delete this event? This action cannot be undone.'}
          </DialogContentText>
          {eventToDelete && (
            <Box mt={2} p={1.5} sx={{ backgroundColor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {eventToDelete.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {new Date(eventToDelete.event_date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Tag Dialog */}
      <EmailTagDialog
        open={email.tagDialogOpen}
        availableTags={email.availableEmailTags}
        selectedTag={email.selectedTag}
        newTagName={email.newTagName}
        loading={email.tagOperationLoading}
        onClose={email.handleCloseTagDialog}
        onAddExistingTag={email.handleAddExistingTag}
        onCreateAndAddTag={email.handleCreateAndAddTag}
        onTagNameChange={email.setNewTagName}
      />

      {/* Task Edit Modal */}
      <Dialog
        open={editTaskModalOpen}
        onClose={() => setEditTaskModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TaskIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Edit Task
            </Typography>
          </Box>
          <IconButton onClick={() => setEditTaskModalOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          {selectedTaskForEdit && (
            <Box>
              {/* Title */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <TaskIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Title
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  value={editedTaskTitle}
                  onChange={(e) => setEditedTaskTitle(e.target.value)}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'grey.50',
                    }
                  }}
                />
              </Box>

              {/* Status, Priority, Due Date, and Assigned To */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Status
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <Select
                      value={editedTaskStatus}
                      onChange={(e) => setEditedTaskStatus(e.target.value)}
                      renderValue={(value) => (
                        <Chip
                          label={getStatusDisplay(value)}
                          size="small"
                          color={STATUS_COLORS[value as keyof typeof STATUS_COLORS] || 'default'}
                          sx={{ height: 24 }}
                        />
                      )}
                    >
                      <MenuItem value="pending">
                        <Chip label="Pending" size="small" color="default" />
                      </MenuItem>
                      <MenuItem value="in_progress">
                        <Chip label="In Progress" size="small" color="info" />
                      </MenuItem>
                      <MenuItem value="completed">
                        <Chip label="Completed" size="small" color="success" />
                      </MenuItem>
                      <MenuItem value="blocked">
                        <Chip label="Blocked" size="small" color="error" />
                      </MenuItem>
                      <MenuItem value="cancelled">
                        <Chip label="Cancelled" size="small" color="warning" />
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Priority
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <Select
                      value={editedTaskPriority}
                      onChange={(e) => setEditedTaskPriority(e.target.value)}
                      renderValue={(value) => (
                        <Chip
                          label={value.charAt(0).toUpperCase() + value.slice(1)}
                          size="small"
                          sx={{ height: 24 }}
                        />
                      )}
                    >
                      <MenuItem value="low">
                        <Chip label="Low" size="small" />
                      </MenuItem>
                      <MenuItem value="medium">
                        <Chip label="Medium" size="small" />
                      </MenuItem>
                      <MenuItem value="high">
                        <Chip label="High" size="small" />
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Due Date
                  </Typography>
                  <TextField
                    type="date"
                    size="small"
                    value={editedTaskDueDate}
                    onChange={(e) => setEditedTaskDueDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 150 }}
                  />
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Assigned To
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <Select
                      value={editedTaskAssignedTo}
                      onChange={(e) => setEditedTaskAssignedTo(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Unassigned</em>
                      </MenuItem>
                      {inline.availableUsers.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          {user.first_name && user.last_name
                            ? `${user.first_name} ${user.last_name}`
                            : user.email}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Description */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <NoteIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Description
                  </Typography>
                </Box>
                <RichTextEditor
                  initialContent={editedTaskDescription}
                  onChange={(content) => setEditedTaskDescription(content)}
                  placeholder="Add task description..."
                  minHeight={200}
                  showSaveButton={false}
                  hideAttachmentButton={true}
                />
              </Box>

              {/* Existing Attachments */}
              {editedTaskExistingAttachments.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Attachments
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {editedTaskExistingAttachments.map((attachment) => (
                      <Chip
                        key={attachment.id}
                        label={attachment.original_filename}
                        onClick={async () => {
                          try {
                            const response = await api.get(attachment.file_url, { headers });
                            const downloadUrl = response.data.url;

                            // For local development, fetch with auth and create blob
                            if (downloadUrl.startsWith('http://localhost') || downloadUrl.startsWith('http://127.0.0.1')) {
                              // Extract the path after /api/
                              const urlPath = downloadUrl.replace('http://localhost:8000/api', '');
                              const fileResponse = await api.get(urlPath, {
                                headers,
                                responseType: 'blob'
                              });

                              // Create blob URL and download
                              const blob = new Blob([fileResponse.data]);
                              const blobUrl = window.URL.createObjectURL(blob);
                              const link = document.createElement('a');
                              link.href = blobUrl;
                              link.download = attachment.original_filename;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              window.URL.revokeObjectURL(blobUrl);
                            } else {
                              // For S3 presigned URLs, direct download
                              const link = document.createElement('a');
                              link.href = downloadUrl;
                              link.download = attachment.original_filename;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }
                          } catch (error) {
                            console.error('Failed to download attachment:', error);
                            alert('Failed to download attachment. Please try again.');
                          }
                        }}
                        onDelete={() => {
                          setEditedTaskAttachmentsToRemove([...editedTaskAttachmentsToRemove, attachment.id]);
                          setEditedTaskExistingAttachments(
                            editedTaskExistingAttachments.filter(a => a.id !== attachment.id)
                          );
                        }}
                        icon={<AttachFileIcon />}
                        size="small"
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* New Files */}
              {editedTaskFiles.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                    New Files ({editedTaskFiles.length})
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {editedTaskFiles.map((file, index) => (
                      <Chip
                        key={index}
                        label={file.name}
                        onDelete={() => {
                          setEditedTaskFiles(editedTaskFiles.filter((_, i) => i !== index));
                        }}
                        size="small"
                        icon={<AttachFileIcon />}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* File Upload */}
              <Button
                component="label"
                variant="outlined"
                size="small"
                startIcon={<AttachFileIcon />}
                sx={{ mb: 2 }}
              >
                Add Files
                <input
                  type="file"
                  multiple
                  hidden
                  onChange={(e) => {
                    if (e.target.files) {
                      setEditedTaskFiles([...editedTaskFiles, ...Array.from(e.target.files)]);
                    }
                  }}
                />
              </Button>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Box>
            <Button
              variant="outlined"
              onClick={() => setEditTaskModalOpen(false)}
              disabled={updatingTask}
            >
              Cancel
            </Button>
          </Box>
          <Button
            variant="contained"
            onClick={async () => {
              if (!selectedTaskForEdit) return;

              setUpdatingTask(true);
              try {
                const formData = new FormData();
                formData.append('title', editedTaskTitle);
                formData.append('description', editedTaskDescription);
                formData.append('status', editedTaskStatus);
                formData.append('priority', editedTaskPriority);
                if (editedTaskDueDate) {
                  formData.append('due_date', editedTaskDueDate);
                }
                if (editedTaskAssignedTo) {
                  formData.append('assigned_to', editedTaskAssignedTo);
                }

                // Add new files
                editedTaskFiles.forEach((file) => {
                  formData.append('attachment_files', file);
                });

                // Add attachments to remove
                if (editedTaskAttachmentsToRemove.length > 0) {
                  editedTaskAttachmentsToRemove.forEach((id) => {
                    formData.append('attachments_to_remove', id);
                  });
                }

                await api.patch(`/tasks/${selectedTaskForEdit.id}/`, formData, {
                  headers: {
                    ...headers,
                    'Content-Type': 'multipart/form-data',
                  },
                });

                // Refresh pending tasks
                const response = await api.get(`/tasks/?case=${caseId}`, { headers });
                const tasks = response.data.results || response.data || [];
                const pendingAndInProgressTasks = tasks.filter((task: any) =>
                  task.status === 'pending' || task.status === 'in_progress'
                );
                const enrichedTasks = pendingAndInProgressTasks.map((task: any) => {
                  const assignedUser = inline.availableUsers.find(u => u.id === task.assigned_to);
                  return {
                    ...task,
                    assigned_to_name: assignedUser
                      ? (assignedUser.first_name && assignedUser.last_name
                        ? `${assignedUser.first_name} ${assignedUser.last_name}`
                        : assignedUser.email)
                      : null
                  };
                });

                // Sort tasks by due date (earliest first), then by created_at
                enrichedTasks.sort((a: any, b: any) => {
                  // Tasks with due dates come before tasks without
                  if (a.due_date && !b.due_date) return -1;
                  if (!a.due_date && b.due_date) return 1;

                  // If both have due dates, sort by due date
                  if (a.due_date && b.due_date) {
                    const dateComparison = new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
                    if (dateComparison !== 0) return dateComparison;
                  }

                  // If same due date (or both null), sort by created_at (oldest first)
                  return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
                });

                inline.setPendingTasks(enrichedTasks);
                inline.setPendingTasksFetched(true); // Keep cache valid

                // Refresh timeline events
                fetchTimelineEvents();

                setEditTaskModalOpen(false);
              } catch (err) {
                console.error('Failed to update task:', err);
                setError('Failed to update task');
              } finally {
                setUpdatingTask(false);
              }
            }}
            disabled={!editedTaskTitle.trim() || updatingTask}
          >
            {updatingTask ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Task Confirmation Dialog */}
      <TaskDeleteDialog
        open={deleteTaskConfirmOpen}
        task={taskToDelete}
        deleting={deletingTask}
        caseId={caseId}
        headers={headers}
        availableUsers={inline.availableUsers}
        onClose={() => {
          setDeleteTaskConfirmOpen(false);
          setTaskToDelete(null);
        }}
        onDeleteStart={() => setDeletingTask(true)}
        onDeleteEnd={() => setDeletingTask(false)}
        onSuccess={(enrichedTasks) => {
          inline.setPendingTasks(enrichedTasks);
          inline.setPendingTasksFetched(true);
        }}
        onError={setError}
        onRefreshTimeline={fetchTimelineEvents}
      />

      {/* Task Edit Dialog (for pending tasks) */}
      <TaskEditDialog
        open={taskEditDialogOpen}
        task={null}
        loading={loadingTaskData}
        headers={headers}
        availableUsers={inline.availableUsers}
        editTaskTitle={editTaskTitle}
        editTaskDescription={editTaskDescription}
        editTaskStatus={editTaskStatus}
        editTaskPriority={editTaskPriority}
        editTaskAssignedTo={editTaskAssignedTo}
        editTaskDueDate={editTaskDueDate}
        editTaskFiles={editTaskFiles}
        editTaskExistingAttachments={editTaskExistingAttachments}
        setEditTaskTitle={setEditTaskTitle}
        setEditTaskDescription={setEditTaskDescription}
        setEditTaskStatus={setEditTaskStatus}
        setEditTaskPriority={setEditTaskPriority}
        setEditTaskAssignedTo={setEditTaskAssignedTo}
        setEditTaskDueDate={setEditTaskDueDate}
        setEditTaskFiles={setEditTaskFiles}
        setEditTaskExistingAttachments={setEditTaskExistingAttachments}
        onClose={handleCloseTaskEditDialog}
        onSave={handleSaveTaskFromDialog}
        onDeleteAttachment={handleDeleteAttachmentFromDialog}
      />
    </Box>
  );
}